import React, { useState, useEffect, useRef } from "react";
import { ref, onValue } from 'firebase/database';
import { database, firestore } from '../../firebaseConfig';
import { doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import BulkModeProgress from './BulkModeComponents/BulkModeProgress';
import BulkGenerationLoading from './BulkModeComponents/BulkGenerationLoading';
import { 
  generateEmailForContact, 
  updateContactStatus,
  saveLastSenderToDatabase 
} from './BulkModeComponents/bulkModeHelpers';
import { CLOUD_FUNCTION_URLS } from '../../utils/cloudFunctions';

// Helper-Funktion für URL-Formatierung
const formatURL = (url, prefix) => {
  if (!url) return '';
  
  // Wenn URL bereits http:// oder https:// enthält
  if (url.match(/^https?:\/\//i)) {
    return url;
  }
  
  // Entferne @ von Instagram-Handles
  if (prefix === 'https://instagram.com/') {
    url = url.replace('@', '');
  }
  
  return `${prefix}${url}`;
};

const BulkModeEmail = ({ 
  onClose, 
  rowData, 
  userId, 
  isReply, 
  source, 
  onEmailResult,
  userSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping,
  bulkContacts,
  allData
}) => {
  // Grundlegende E-Mail-States
  const [selectedTopic, setSelectedTopic] = useState("Introduce Yourself");
  const [selectedStyle, setSelectedStyle] = useState("Professional & Supportive");
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  
  // Bulk-Modus-spezifische States
  const [currentBulkIndex, setCurrentBulkIndex] = useState(0);
  const [currentBulkContact, setCurrentBulkContact] = useState(null);
  const [bulkGeneratedEmails, setBulkGeneratedEmails] = useState({});
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [bulkGenerationProgress, setBulkGenerationProgress] = useState(0);
  const [bulkGenerationComplete, setBulkGenerationComplete] = useState(false);
  const [sentEmails, setSentEmails] = useState({});
  
  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);
  // Ref für die Bulk-Generierung
  const hasStartedBulkGeneration = useRef(false);
  // Ref für paralleles Senden
  const pendingSends = useRef({});

  // State für Session-Statistiken
  const [sessionStats, setSessionStats] = useState({
    sent: 0,
    skipped: 0,
    deleted: 0
  });
  
  // Refs für akkurate Zählung
  const sessionDeletedRef = useRef(0);
  const sessionSentRef = useRef(0);
  const sessionSkippedRef = useRef(0);
  
  // Neuer State für die aktualisierte Liste der Bulk-Kontakte
  const [updatedBulkContacts, setUpdatedBulkContacts] = useState(bulkContacts);
  
  // States um zu verfolgen, ob der Benutzer manuell Änderungen vorgenommen hat
  const [userEditedSubject, setUserEditedSubject] = useState(false);
  const [userEditedBody, setUserEditedBody] = useState(false);
  const [lastContactId, setLastContactId] = useState(null);

  // Initialisiere die aktualisierte Kontaktliste
  useEffect(() => {
    setUpdatedBulkContacts(bulkContacts);
  }, [bulkContacts]);

  // Bestimmt das passende Topic anhand des Kontakt-Status oder AI Tags
  const determineTopicForContact = async (contact) => {
    if (!contact) return "Introduce Yourself";
    
    console.log('📧 [BULK-MODE] determineTopicForContact for:', contact.business_name || 'Unnamed', contact.id);
    
    // 1. Prüfe zuerst, ob der Kontakt AI Tags hat (höchste Priorität)
    if (contact.tags && typeof contact.tags === 'object') {
      console.log('📧 [BULK-MODE] AI Tags found in contact:', contact.tags);
      
      // Konvertiere Tags-Objekt zu Array und sortiere nach appliedAt (neueste zuerst)
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        console.log('📧 [BULK-MODE] 🔍 SORTING DEBUG:');
        console.log('📧 [BULK-MODE]   Tag A:', { id: a.id, appliedAt: a.appliedAt, parsedDate: dateA.toISOString(), timestamp: dateA.getTime() });
        console.log('📧 [BULK-MODE]   Tag B:', { id: b.id, appliedAt: b.appliedAt, parsedDate: dateB.toISOString(), timestamp: dateB.getTime() });
        console.log('📧 [BULK-MODE]   Sort result (dateB - dateA):', dateB.getTime() - dateA.getTime());
        return dateB - dateA; // Neueste zuerst
      });
      
      console.log('📧 [BULK-MODE] Tags array sorted by date:', tagsArray);
      console.log('📧 [BULK-MODE] 🎯 SELECTED TAG (index 0):', {
        id: tagsArray[0]?.id,
        appliedAt: tagsArray[0]?.appliedAt,
        appliedTopic: tagsArray[0]?.appliedTopic,
        selectedTopic: tagsArray[0]?.selectedTopic,
        isNewest: true
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        console.log('📧 [BULK-MODE] Newest tag:', newestTag);
        
        // 🔥 WICHTIG: Immer die neueste Version aus userTopics laden (nicht vom Kontakt)
        if (newestTag.id) {
          console.log('📧 [BULK-MODE] Searching for AI Tag with ID:', newestTag.id);
          console.log('📧 [BULK-MODE] Available userTopics keys:', Object.keys(userTopics));
          
          // 🔥 ZEIGE ALLE TOPICS MIT IHREN AI TAG IDS
          console.log('📧 [BULK-MODE] 🔍 COMPLETE userTopics breakdown:');
          Object.entries(userTopics).forEach(([topicKey, topicData]) => {
            console.log(`📧 [BULK-MODE]   Topic Key: ${topicKey}`);
            console.log(`📧 [BULK-MODE]   Topic Data:`, {
              topic: topicData.topic,
              aiTagId: topicData.aiTagId,
              type: topicData.type,
              subjectLine: topicData.subjectLine,
              createdAt: topicData.createdAt
            });
          });
          
          // 🔥 NEUE LOGIK: Lade die AI Tag Settings und hole selectedTopic von dort!
          // Problem: Der gespeicherte Tag hat kein selectedTopic, aber die AI Tag Settings haben es
          
          // Lade die AI Tag-Einstellungen
          const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
          const aiTagSnapshot = await new Promise((resolve) => {
            onValue(aiTagRef, resolve, { onlyOnce: true });
          });
          
          if (aiTagSnapshot.exists()) {
            const aiTagData = aiTagSnapshot.val();
            console.log('📧 [BULK-MODE] ✅ Loaded AI Tag settings from database:', {
              selectedTopic: aiTagData.selectedTopic,
              tag: aiTagData.tag,
              name: aiTagData.name
            });
            
            if (aiTagData.selectedTopic) {
              console.log('📧 [BULK-MODE] ✅ Found selectedTopic from AI Tag settings (like Normal-Mode):', aiTagData.selectedTopic);
              console.log('📧 [BULK-MODE] 📝 Note: Using AI Tag selectedTopic from settings, not from contact tag');
              return aiTagData.selectedTopic;
            }
          } else {
            console.log('📧 [BULK-MODE] ⚠️ AI Tag settings not found for ID:', newestTag.id);
          }
          
          // 🔥 FALLBACK: Prüfe, ob der AI Tag ein selectedTopic hat (falls es doch gespeichert wurde)
          if (newestTag.selectedTopic) {
            console.log('📧 [BULK-MODE] ✅ Found selectedTopic directly from stored tag:', newestTag.selectedTopic);
            return newestTag.selectedTopic;
          }
        }
        
        // Fallback: Prüfe, ob der gespeicherte Tag ein selectedTopic hat (alte Kopie)
        if (newestTag.selectedTopic) {
          console.log('📧 [BULK-MODE] ✅ Using selectedTopic from stored tag (legacy):', newestTag.selectedTopic);
          return newestTag.selectedTopic;
        }
      } else {
        console.log('📧 [BULK-MODE] No AI tags found in contact');
      }
    } else {
      console.log('📧 [BULK-MODE] No AI tags found in contact');
    }
    
    // 2. Fallback: Prüfe auch die alte aiTags Struktur (falls noch vorhanden)
    if (contact.aiTags && Array.isArray(contact.aiTags) && contact.aiTags.length > 0) {
      console.log('📧 [BULK-MODE] Checking legacy aiTags structure:', contact.aiTags);
      const lastTag = contact.aiTags[contact.aiTags.length - 1];
      if (lastTag.selectedTopic) {
        console.log('📧 [BULK-MODE] ✅ Using selectedTopic from legacy aiTags:', lastTag.selectedTopic);
        return lastTag.selectedTopic;
      }
    }
    
    console.log('📧 [BULK-MODE] No AI tag topics found, using status-based logic');
    
    // 3. Fallback: Verwende die bisherige Status-basierte Logik
    const contactStatus = contact.lead_contacted || contact.lead_status || "";
    console.log('📧 [BULK-MODE] Contact status:', contactStatus);
    
    // Entscheidungslogik für das Topic basierend auf dem Status
    if (!contactStatus || contactStatus === "Not Contacted" || contactStatus === "New" || contactStatus === "Import") {
      console.log('📧 [BULK-MODE] Status indicates "Introduce Yourself"');
      return "Introduce Yourself";
    } else if (contactStatus === "Introduced") {
      console.log('📧 [BULK-MODE] Status indicates "Follow Up"');
      return "Follow Up";
    } else if (contactStatus === "First Follow-up" || contactStatus === "1. Follow-UP") {
      console.log('📧 [BULK-MODE] Status indicates "Second Follow Up"');
      return "Second Follow Up";
    } else if (contactStatus === "Second Follow-up" || contactStatus === "2. Follow-UP") {
      console.log('📧 [BULK-MODE] Status indicates "Third Follow Up"');
      return "Third Follow Up";
    } else {
      // Fallback für unbekannte Status
      console.log('📧 [BULK-MODE] Unknown status, defaulting to "Introduce Yourself"');
      return "Introduce Yourself";
    }
  };

  // Initialisiere den aktuellen Kontakt
  useEffect(() => {
    if (updatedBulkContacts.length > 0 && isTopicsLoaded) {
      const contactId = updatedBulkContacts[currentBulkIndex];
      const contact = allData.find(row => row.id === contactId);
      if (contact) {
        setCurrentBulkContact(contact);
        
        // 🔥 WICHTIG: Setze das passende Topic basierend auf dem Kontaktstatus ODER AI Tags
        determineTopicForContact(contact).then(topic => {
          console.log('📧 [BULK-CONTACT-CHANGE] Setting topic for contact:', contact.business_name, 'Topic:', topic);
          setSelectedTopic(topic);
        });
      }
    }
  }, [updatedBulkContacts, currentBulkIndex, allData, isTopicsLoaded]);
  
  // Initialisiere die Absender-E-Mail und starte die Bulk-Generierung
  useEffect(() => {
    if (senderEmails.length > 0) {
      // Setze den letzten verwendeten Absender
      const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
      onValue(lastSenderRef, (snapshot) => {
        if (snapshot.exists()) {
          const lastSender = snapshot.val();
          const matchingEmail = senderEmails.find(email => email.email === lastSender.email);
          if (matchingEmail) {
            setSelectedSenderEmail(matchingEmail.email);
            return;
          }
        }
        // Fallback auf die erste E-Mail
        setSelectedSenderEmail(senderEmails[0].email);
      }, { onlyOnce: true });
    }
    
    // Starte automatisch die Bulk-Generierung, wenn alle Bedingungen erfüllt sind
    if (
      updatedBulkContacts.length > 0 && 
      isTopicsLoaded && 
      !hasStartedBulkGeneration.current && 
      !isBulkGenerating && 
      senderEmails.length > 0
    ) {
      hasStartedBulkGeneration.current = true;
      // Kurze Verzögerung, um sicherzustellen, dass alle Abhängigkeiten geladen sind
      setTimeout(() => {
        generateAllBulkEmails();
      }, 300);
    }
  }, [senderEmails, userId, updatedBulkContacts, isTopicsLoaded, isBulkGenerating]);

  // Aktualisiere den Kontext, wenn sich der Kontakt ändert
  useEffect(() => {
    if (currentBulkContact) {
      // Setze den Kontext für den aktuellen Kontakt
      const contextText = `
Business Name: ${currentBulkContact.business_name || ''}
City: ${currentBulkContact.lead_city || ''}
Description: ${currentBulkContact.lead_description || ''}
Type: ${currentBulkContact.lead_type || ''}
Website: ${currentBulkContact.lead_website || ''}
      `.trim();
      setContextInfo(contextText);
      
      // Aktualisiere die E-Mail-Adresse
      const emailAddresses = currentBulkContact.lead_email 
        ? currentBulkContact.lead_email.split(',').map(email => email.trim()).filter(Boolean)
        : [];
      
      if (emailAddresses.length > 0) {
        setSelectedEmail(emailAddresses[0]);
        
        // Setze CC-Emails initial auf leer statt automatisch hinzuzufügen
        // Benutzer kann CC-Empfänger manuell hinzufügen
        setCcEmails([]);
      } else {
        setSelectedEmail("");
        setCcEmails([]);
      }
      
      // Setze den Sending-Status zurück beim Kontakt-Wechsel
      setSendSuccess(false);
      setIsSending(false);
    }
  }, [currentBulkContact]);

  // Aktualisiere die Betreffzeile und den E-Mail-Body, wenn sich der Kontakt ändert
  useEffect(() => {
    if (!currentBulkContact) return;

    // Prüfe, ob sich der Kontakt geändert hat
    const contactChanged = lastContactId !== currentBulkContact.id;
    
    // Immer den Erfolgs-Status zurücksetzen
    setSendSuccess(false);
    
    if (bulkGeneratedEmails[currentBulkContact.id]) {
      // Lade die generierte E-Mail
      const generatedEmail = bulkGeneratedEmails[currentBulkContact.id];
      if (!generatedEmail.error) {
        // Nur überschreiben wenn der Kontakt gewechselt hat oder der Benutzer nichts bearbeitet hat
        if (contactChanged || !userEditedSubject) {
          updateSubjectInputDirectly(generatedEmail.subject || "");
        }
        // Nur setzen, wenn der Body noch leer ist oder wenn ein Kontaktwechsel stattgefunden hat und der Benutzer nichts bearbeitet hat
        if (contactChanged && !userEditedBody) {
          setBodyContent(generatedEmail.body || "");
        } else if (bodyContent === "" || !bodyContent) {
          setBodyContent(generatedEmail.body || "");
        }
      } else {
        // Bei Fehlern Standardwerte verwenden, aber nur wenn der Benutzer nichts bearbeitet hat
        if (contactChanged || !userEditedSubject) {
          // 🔥 WICHTIG: Verwende das aktuelle selectedTopic für die Subject Line
          determineTopicForContact(currentBulkContact).then(topic => {
            console.log('📧 [BULK-SUBJECT-UPDATE] Using topic for subject:', topic, 'for contact:', currentBulkContact.business_name);
            
            // Temporär das Topic setzen für die Subject Line Generierung
            const tempTopic = selectedTopic;
            setSelectedTopic(topic);
            
            setTimeout(() => {
              updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact));
              // Topic zurücksetzen falls es anders war
              if (tempTopic !== topic) {
                setSelectedTopic(topic);
              }
            }, 10);
          });
        }
        // Nur setzen, wenn der Body noch leer ist und der Benutzer nichts bearbeitet hat
        if ((bodyContent === "" || !bodyContent) && !userEditedBody) {
          setBodyContent("");
        }
      }
    } else {
      // Wenn noch keine E-Mail generiert wurde, setze Standardwerte nur wenn der Benutzer nichts bearbeitet hat
      if (contactChanged || !userEditedSubject) {
        // 🔥 WICHTIG: Verwende das korrekte Topic für die Subject Line
        determineTopicForContact(currentBulkContact).then(topic => {
          console.log('📧 [BULK-SUBJECT-NEW] Using topic for new subject:', topic, 'for contact:', currentBulkContact.business_name);
          
          // Stelle sicher, dass das selectedTopic korrekt ist
          if (selectedTopic !== topic) {
            setSelectedTopic(topic);
          }
          
          setTimeout(() => {
            updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact));
          }, 10);
        });
      }
      // Nur setzen, wenn der Body noch leer ist und der Benutzer nichts bearbeitet hat
      if ((bodyContent === "" || !bodyContent) && !userEditedBody) {
        setBodyContent("");
      }
    }
    
    // Aktualisiere die letzte Kontakt-ID und setze die Edit-Flags zurück bei Kontaktwechsel
    if (contactChanged) {
      setLastContactId(currentBulkContact.id);
      setUserEditedSubject(false);
      setUserEditedBody(false);
    }
  }, [currentBulkContact, bulkGeneratedEmails, selectedTopic, userTopics]);

  // Betreffzeile aus Topic-Einstellungen oder Standard-Templates holen
  const getTopicSubjectLine = (contact) => {
    if (!contact) return "BonusClub";
    
    console.log('📧 [BULK-SUBJECT] Getting subject line for contact:', contact.business_name);
    console.log('📧 [BULK-SUBJECT] selectedTopic:', selectedTopic);
    
    // 🔥 DIREKTE AI TAG PRÜFUNG: Genau wie im Normal-Modus
    // Prüfe, ob der Kontakt AI Tags mit selectedTopic hat
    if (contact.tags && typeof contact.tags === 'object') {
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        return dateB - dateA;
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        if (newestTag.selectedTopic && newestTag.selectedTopic === selectedTopic) {
          console.log('📧 [BULK-SUBJECT] Topic matches AI Tag selectedTopic, searching userTopics...');
          
          // Suche nach dem Topic in userTopics (wie im Normal-Modus)
          const matchingTopic = Object.values(userTopics).find(topicData => {
            return topicData.topic === selectedTopic || topicData.type === selectedTopic;
          });
          
          if (matchingTopic && matchingTopic.subjectLine) {
            // Platzhalter ersetzen
            let subject = matchingTopic.subjectLine;
            subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
            subject = subject.replace(/{{name}}/g, contact.lead_firstname || 'dort');
            subject = subject.replace(/{{company}}/g, 'BonusClub');
            console.log('📧 [BULK-SUBJECT] ✅ Using AI Tag subject line:', subject);
            return subject;
          }
        }
      }
    }
    
    console.log('📧 [BULK-SUBJECT] Using standard logic');
    
    // Fallback: Standard-Logik
    const topicType = topicTypeMapping[selectedTopic];
    if (topicType) {
      const matchingTopic = Object.values(userTopics).find(topic => topic.type === topicType);
      if (matchingTopic && matchingTopic.subjectLine) {
        let subject = matchingTopic.subjectLine;
        subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
        subject = subject.replace(/{{name}}/g, contact.lead_firstname || 'dort');
        subject = subject.replace(/{{company}}/g, 'BonusClub');
        return subject;
      }
    }
    
    // Standard-Betreffzeilen
    return getDefaultSubject(contact);
  };

  // Standard-Betreff für einen Kontakt
  const getDefaultSubject = (contact) => {
    if (!contact) return "BonusClub";
    
    // Standard-Betreffzeilen mit dem aktuellen Kontakt
    const defaultSubjects = {
      "Introduce Yourself": `Boost für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Follow Up": `Nachfrage - Partnerschaft mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      "Second Follow Up": `Kurzes Update für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Third Follow Up": `Finale Nachfrage für ${contact.business_name || 'Ihr Unternehmen'}`
    };
    
    return defaultSubjects[selectedTopic] || `BonusClub für ${contact.business_name || 'Ihr Unternehmen'}`;
  };

  // Hilfsfunktion für Standard-Topic-Anweisungen
  const getDefaultTopicNote = (topicType) => {
    const defaultNotes = {
      'intro': "Bitte stelle unser Unternehmen vor, teile einige spannende Details darüber, wie wir helfen können, und füge einen Call-to-Action hinzu.",
      'follow_up_1': "Erkundige dich höflich, ob der Empfänger die vorherige E-Mail erhalten hat. Halte es kurz und freundlich.",
      'follow_up_2': "Dies ist das zweite Follow-up. Frage höflich nach, ob der Empfänger noch interessiert ist, ohne aufdringlich zu sein.",
      'follow_up_3': "Dies ist das letzte Follow-up. Mache einen letzten sanften Versuch, den Empfänger zu erreichen.",
      'custom_reply': "Bitte verfasse eine durchdachte Antwort auf die Nachricht des Empfängers."
    };
    
    return defaultNotes[topicType] || "Bitte schreibe eine professionelle E-Mail an den Empfänger.";
  };
  
  // Generiere die E-Mail für einen einzelnen Kontakt (neu generieren)
  const handleRegenerateForCurrentContact = async () => {
    if (!currentBulkContact || isGenerating) return;
    
    setIsGenerating(true);
    // Edit-Flags zurücksetzen, da der Benutzer explizit neu generieren möchte
    setUserEditedSubject(false);
    setUserEditedBody(false);
    
    try {
      // 🔥 WICHTIG: Bestimme das Topic für den aktuellen Kontakt
      const contactTopic = await determineTopicForContact(currentBulkContact);
      console.log('📧 [BULK-REGENERATE] Contact:', currentBulkContact.business_name, 'Topic:', contactTopic);
      
      // Generiere die E-Mail für diesen Kontakt mit dem kontaktspezifischen Topic
      const emailContent = await generateEmailForContact(
        currentBulkContact, 
        contactTopic, // 🔥 Verwende das kontaktspezifische Topic
        selectedStyle, 
        contextInfo, 
        userId, 
        userSettings, 
        aiSettings, 
        userTopics, 
        topicTypeMapping, 
        getDefaultTopicNote
      );
      
      // Aktualisiere die generierten E-Mails
      setBulkGeneratedEmails(prev => ({
        ...prev,
        [currentBulkContact.id]: emailContent
      }));
      
      // Aktualisiere die Betreffzeile und den Body
      updateSubjectInputDirectly(emailContent.subject || "");
      setBodyContent(emailContent.body || "");
      
    } catch (error) {
      console.error(`Error generating email for contact ${currentBulkContact.id}:`, error);
      
      // Bei einem Fehler Standardwerte verwenden
      updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact));
      
      // Fehler in den generierten E-Mails speichern
      setBulkGeneratedEmails(prev => ({
        ...prev,
        [currentBulkContact.id]: { 
          error: true, 
          message: error.message,
          subject: getTopicSubjectLine(currentBulkContact)
        }
      }));
    } finally {
      setIsGenerating(false);
    }
  };

  // Generiere Bulk-E-Mails für alle Kontakte beim Start
  const generateAllBulkEmails = async () => {
    if (updatedBulkContacts.length === 0 || isBulkGenerating) return;
    
    setIsBulkGenerating(true);
    setBulkGenerationProgress(0);
    
    const generatedEmails = {};
    let progress = 0;
    
    // Bestimme die Anzahl der sofort zu ladenden Kontakte (maximal 5)
    const initialLoadCount = Math.min(5, updatedBulkContacts.length);
    const initialContactIds = updatedBulkContacts.slice(0, initialLoadCount);
    const remainingContactIds = updatedBulkContacts.slice(initialLoadCount);
    
    // Zuerst die ersten 5 Kontakte laden
    for (const contactId of initialContactIds) {
      const contact = allData.find(row => row.id === contactId);
      if (contact) {
        try {
          // 🔥 WICHTIG: Bestimme das Topic für jeden Kontakt einzeln
          const contactTopic = await determineTopicForContact(contact);
          console.log('📧 [BULK-GENERATION] Contact:', contact.business_name, 'Topic:', contactTopic);
          
          // Generiere die E-Mail für diesen Kontakt mit dem kontaktspezifischen Topic
          const emailContent = await generateEmailForContact(
            contact, 
            contactTopic, // 🔥 Verwende das kontaktspezifische Topic
            selectedStyle, 
            // Erstelle für jeden Kontakt einen spezifischen Kontext
            `
Business Name: ${contact.business_name || ''}
City: ${contact.lead_city || ''}
Description: ${contact.lead_description || ''}
Type: ${contact.lead_type || ''}
Website: ${contact.lead_website || ''}
            `.trim(), 
            userId, 
            userSettings, 
            aiSettings, 
            userTopics,
            topicTypeMapping, 
            getDefaultTopicNote
          );
          generatedEmails[contactId] = emailContent;
        } catch (error) {
          console.error(`Error generating email for contact ${contactId}:`, error);
          // Fehler-Eintrag
          generatedEmails[contactId] = { 
            error: true, 
            message: error.message,
            subject: getTopicSubjectLine(contact)
          };
        }
        
        // Aktualisiere den Fortschritt
        progress = Math.round(((Object.keys(generatedEmails).length) / updatedBulkContacts.length) * 100);
        setBulkGenerationProgress(progress);
      }
    }
    
    // Lade die erste E-Mail, sobald die ersten 5 verfügbar sind
    if (updatedBulkContacts.length > 0 && currentBulkContact) {
      const generatedEmail = generatedEmails[currentBulkContact.id];
      if (generatedEmail && !generatedEmail.error) {
        updateSubjectInputDirectly(generatedEmail.subject || "");
        setBodyContent(generatedEmail.body || "");
      }
    }
    
    // Setze isBulkGenerating auf false, um die Benutzeroberfläche zu entsperren
    // aber behalte den Fortschrittsbalken
    setIsBulkGenerating(false);
    
    // Jetzt den Rest im Hintergrund laden
    if (remainingContactIds.length > 0) {
      // Starte einen asynchronen Prozess für die restlichen Kontakte
      (async () => {
        for (const contactId of remainingContactIds) {
          const contact = allData.find(row => row.id === contactId);
          if (contact) {
            try {
              // 🔥 WICHTIG: Bestimme das Topic für jeden Kontakt einzeln
              const contactTopic = await determineTopicForContact(contact);
              console.log('📧 [BULK-GENERATION-BG] Contact:', contact.business_name, 'Topic:', contactTopic);
              
              // Generiere die E-Mail für diesen Kontakt mit dem kontaktspezifischen Topic
              const emailContent = await generateEmailForContact(
                contact, 
                contactTopic, // 🔥 Verwende das kontaktspezifische Topic
                selectedStyle, 
                // Erstelle für jeden Kontakt einen spezifischen Kontext
                `
Business Name: ${contact.business_name || ''}
City: ${contact.lead_city || ''}
Description: ${contact.lead_description || ''}
Type: ${contact.lead_type || ''}
Website: ${contact.lead_website || ''}
                `.trim(), 
                userId, 
                userSettings, 
                aiSettings, 
                userTopics,
                topicTypeMapping, 
                getDefaultTopicNote
              );
              // Aktualisiere den State mit dem neuen Ergebnis
              setBulkGeneratedEmails(prev => ({
                ...prev,
                [contactId]: emailContent
              }));
            } catch (error) {
              console.error(`Error generating email for contact ${contactId}:`, error);
              // Fehler-Eintrag
              setBulkGeneratedEmails(prev => ({
                ...prev,
                [contactId]: { 
                  error: true, 
                  message: error.message,
                  subject: getTopicSubjectLine(contact)
                }
              }));
            }
            
            // Aktualisiere den Fortschritt
            const totalProcessed = initialLoadCount + remainingContactIds.indexOf(contactId) + 1;
            const newProgress = Math.round((totalProcessed / updatedBulkContacts.length) * 100);
            setBulkGenerationProgress(newProgress);
          }
        }
        
        // Wenn alle E-Mails generiert wurden, setze den Status auf abgeschlossen
        setBulkGenerationComplete(true);
      })();
    } else {
      // Wenn keine weiteren Kontakte zu laden sind, setze den Status auf abgeschlossen
      setBulkGenerationComplete(true);
    }
    
    // Speichere die ersten generierten E-Mails im State
    setBulkGeneratedEmails(generatedEmails);
  };

  // Modifizierte onClose-Funktion für Statistik-Toast
  const handleClose = () => {
    // Nimm die Werte aus den Refs für die aktuellsten Zahlen
    const sent = sessionSentRef.current;
    const skipped = sessionSkippedRef.current;
    const deleted = sessionDeletedRef.current;
    
    // Zeige Toast mit Statistiken
    if (sent > 0 || skipped > 0 || deleted > 0) {
      // Formatiere eine aussagekräftige Nachricht auf Englisch
      let message = "Bulk session summary: ";
      const parts = [];
      
      // Stelle die gelöschten Kontakte zuerst dar, wenn vorhanden
      if (deleted > 0) {
        parts.push(`${deleted} contact${deleted === 1 ? '' : 's'} deleted`);
      }
      
      if (sent > 0) {
        parts.push(`${sent} email${sent === 1 ? '' : 's'} sent`);
      }
      
      if (skipped > 0) {
        parts.push(`${skipped} skipped`);
      }
      
      // Wenn keine E-Mails gesendet wurden, aber Kontakte gelöscht wurden
      if (sent === 0 && deleted > 0) {
        message = "Contacts deleted: " + deleted;
      } else {
        message += parts.join(', ');
      }
      
      // Zeige den Toast an
      if (onEmailResult) {
        onEmailResult('summary', message);
      }
    }
    
    // Statistiken zurücksetzen, damit alte gesendete E-Mails nicht bei späteren Löschungen angezeigt werden
    sessionSentRef.current = 0;
    sessionSkippedRef.current = 0;
    sessionDeletedRef.current = 0;
    setSessionStats({
      sent: 0,
      skipped: 0,
      deleted: 0
    });
    
    // Originale onClose-Funktion aufrufen
    onClose();
  };

  // Kontakt in Firestore löschen
  const handleDeleteContact = async (contactId) => {
    if (!userId || !contactId) return;
    
    try {
      // Speichere den aktuellen Index vor dem Löschen
      const currentPos = currentBulkIndex;
      
      // Kontakt in Firestore löschen
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await deleteDoc(contactDocRef);
      
      // Lösch-Event an parent, aber nur ID String -> keine Toast-Fehler
      if (onEmailResult) {
        onEmailResult('contact-deleted', String(contactId));
      }
      
      // Erhöhe den Ref zuerst, dann aktualisiere den State
      sessionDeletedRef.current += 1;
      setSessionStats(prev => ({
        ...prev,
        deleted: sessionDeletedRef.current
      }));
      
      // Aktualisiere die lokale Liste der Bulk-Kontakte
      const updatedContacts = updatedBulkContacts.filter(id => id !== contactId);
      setUpdatedBulkContacts(updatedContacts);
      
      // Wenn keine Kontakte mehr übrig sind, schließe den Bulk-Modus sofort
      if (updatedContacts.length === 0) {
        handleClose();
        return;
      }
      
      // Prüfe, ob alle verbliebenen Kontakte bereits gesendet wurden
      const allRemainingSent = updatedContacts.every(id => sentEmails[id]);
      
      // Wenn alle verbleibenden Kontakte bereits eine E-Mail haben, schließe den Modal
      if (allRemainingSent) {
        console.log("Alle verbliebenen Kontakte wurden bereits bearbeitet, schließe Modal");
        handleClose();
        return;
      }
      
      // Finde den nächsten ungesendeten Kontakt (falls vorhanden)
      let nextUnsent = -1;
      for (let i = 0; i < updatedContacts.length; i++) {
        if (!sentEmails[updatedContacts[i]]) {
          nextUnsent = i;
          break;
        }
      }
      
      // Wenn wir einen ungesendeten Kontakt gefunden haben, springe direkt dorthin
      if (nextUnsent !== -1) {
        setCurrentBulkIndex(nextUnsent);
        return;
      }
      
      // Ansonsten bleibe an der gleichen Position oder wähle den letzten Kontakt
      const newIndex = Math.min(currentPos, updatedContacts.length - 1);
      setCurrentBulkIndex(newIndex);
      
    } catch (error) {
      console.error("Error deleting contact:", error);
      // Keine Fehlermeldung anzeigen
    }
  };

  // Aktualisiere die Funktion, um mit der aktualisierten Kontaktliste zu arbeiten
  const goToNextBulkContact = () => {
    if (currentBulkIndex < updatedBulkContacts.length - 1) {
      // Status explizit zurücksetzen
      setSendSuccess(false);
      setIsSending(false);
      
      // Edit-Flags zurücksetzen für den nächsten Kontakt
      setUserEditedSubject(false);
      setUserEditedBody(false);
      
      // Index aktualisieren
      const nextIndex = currentBulkIndex + 1;
      setCurrentBulkIndex(nextIndex);
      
      // 🔥 WICHTIG: Das korrekte Topic für den nächsten Kontakt setzen
      const nextContactId = updatedBulkContacts[nextIndex];
      const nextContact = allData.find(row => row.id === nextContactId);
      if (nextContact) {
        determineTopicForContact(nextContact).then(topic => {
          console.log('📧 [BULK-NEXT] Setting topic for next contact:', nextContact.business_name, 'Topic:', topic);
          setSelectedTopic(topic);
        });
      }
    }
  };
  
  // Aktualisiere die Funktion, um mit der aktualisierten Kontaktliste zu arbeiten
  const goToPreviousBulkContact = () => {
    if (currentBulkIndex > 0) {
      // Prüfe, ob der vorherige Kontakt bereits eine gesendete E-Mail hat
      const prevContactId = updatedBulkContacts[currentBulkIndex - 1];
      
      // Wenn die E-Mail bereits gesendet wurde, nicht zurückspringen
      if (sentEmails[prevContactId]) {
        return;
      }
      
      // Status explizit zurücksetzen
      setSendSuccess(false);
      setIsSending(false);
      
      // Edit-Flags zurücksetzen für den vorherigen Kontakt
      setUserEditedSubject(false);
      setUserEditedBody(false);
      
      // Index aktualisieren
      const prevIndex = currentBulkIndex - 1;
      setCurrentBulkIndex(prevIndex);
      
      // 🔥 WICHTIG: Das korrekte Topic für den vorherigen Kontakt setzen
      const contactId = updatedBulkContacts[prevIndex];
      const prevContact = allData.find(row => row.id === contactId);
      if (prevContact) {
        determineTopicForContact(prevContact).then(topic => {
          console.log('📧 [BULK-PREV] Setting topic for previous contact:', prevContact.business_name, 'Topic:', topic);
          setSelectedTopic(topic);
        });
      }
    }
  };
  
  // Prüfe, ob zu einem Kontakt zurückgesprungen werden kann
  const canGoToPrevious = () => {
    if (currentBulkIndex <= 0) return false;
    
    // Prüfe, ob der vorherige Kontakt bereits eine gesendete E-Mail hat
    const prevContactId = updatedBulkContacts[currentBulkIndex - 1];
    return !sentEmails[prevContactId];
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // E-Mail senden und sofort zur nächsten weitergehen
  const handleSend = async () => {
    if (!currentBulkContact || !selectedEmail) {
      console.error("No email address found for this contact");
      onEmailResult('error', 'No email address found for this contact');
      return;
    }

    const currentId = currentBulkContact.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentCcEmails = [...ccEmails];
    const currentSenderEmail = selectedSenderEmail;
    const currentBusinessName = currentBulkContact.business_name || currentBulkContact.lead_name || 'Unbekannt';

    setIsSending(true);
    
    // Bestimme den neuen Kontaktstatus
    let newStatus = null;
    if (selectedTopic === "Introduce Yourself") {
      newStatus = "Introduced";
    } else if (selectedTopic === "Follow Up") {
      newStatus = "First Follow-up";
    } else if (selectedTopic === "Second Follow Up") {
      newStatus = "Second Follow-up";
    } else if (selectedTopic === "Third Follow Up") {
      newStatus = "Third Follow-up";
    }
    
    // Optimistische UI-Aktualisierung
    setSendSuccess(true);
    
    // Statistik für gesendete E-Mails erhöhen (sowohl im State als auch im Ref)
    sessionSentRef.current += 1;
    setSessionStats(prev => ({
      ...prev,
      sent: sessionSentRef.current
    }));
    
    // Markiere diese E-Mail als gesendet
    setSentEmails(prev => ({
      ...prev,
      [currentId]: {
        timestamp: Date.now(),
        status: newStatus
      }
    }));
    
    // Optimistischer Statuswechsel zum nächsten Kontakt!
    // Wenn wir nicht den letzten Kontakt haben, gehen wir sofort weiter
    if (currentBulkIndex < updatedBulkContacts.length - 1) {
      // Speichere die aktuelle E-Mail-ID zum Tracking
      pendingSends.current[currentId] = true;
      
      // Sofort zum nächsten Kontakt wechseln!
      setTimeout(() => {
        goToNextBulkContact();
        setIsSending(false);
      }, 300);
    } else {
      // Bei der letzten E-Mail etwas warten und dann schließen
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
    
    // Aktualisiere den Kontaktstatus
    if (newStatus) {
      updateContactStatus(userId, currentId, newStatus, onEmailResult)
        .catch(error => console.error("Error updating contact status:", error));
    }
    
    // 🔥 NEU: Prüfe und setze AI Tag basierend auf dem verwendeten Topic
    setAiTagAfterEmailSend(selectedTopic, currentId)
      .catch(error => console.error("Error setting AI tag after email send:", error));
    
    // Callback für EmailResult
    if (onEmailResult) {
      onEmailResult('success', `Sending email to ${currentBusinessName}...`, currentBulkContact);
    }
    
    // E-Mail im Hintergrund senden (kein await, damit es parallel läuft)
    (async () => {
      try {
        // Absender-E-Mail finden
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        
        if (!senderEmailData) {
          throw new Error("The selected sender email was not found.");
        }
        
        // E-Mail formatieren
        const formattedContent = convertToHtml(currentContent, userSettings);
        let contentWithFooter = addFooterToEmail(formattedContent, userSettings);
        
        // Request-Daten erstellen
        const requestData = {
          recipients: [currentSelectedEmail, ...currentCcEmails],
          cc: currentCcEmails,
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: currentBusinessName,
          leadId: currentId,
          emailType: topicTypeMapping[selectedTopic] || "",
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id
        };
        
        // E-Mail senden
        const response = await fetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          console.error('Error parsing JSON response:', e);
          if (onEmailResult) {
            onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`);
          }
          return;
        }
        
        // Entferne diese E-Mail aus den ausstehenden Sendungen
        delete pendingSends.current[currentId];
        
        if (data.success) {
          // Erfolg
          if (onEmailResult) {
            // Prüfe, ob die E-Mail verzögert wurde und eine geplante Zeit hat
            if (data.delayed && data.scheduledTime) {
              // scheduledTime in einen gültigen Timestamp konvertieren
              let scheduledTimeStamp = null;
              try {
                scheduledTimeStamp = new Date(data.scheduledTime).getTime();
                console.log(`Geplante Zeit konvertiert: ${data.scheduledTime} -> ${scheduledTimeStamp}`);
              } catch (e) {
                console.error("Error converting scheduled time:", e);
              }
              
              onEmailResult('success', `Email to ${currentBusinessName} scheduled`, currentBulkContact, scheduledTimeStamp, currentSelectedEmail);
              console.log(`Email scheduled to be sent at: ${data.scheduledTime} to: ${currentSelectedEmail}`);
            } else {
              onEmailResult('success', `Email to ${currentBusinessName} successfully sent`, currentBulkContact, null, currentSelectedEmail);
            }
          }
          
          // Letzten Absender speichern
          await saveLastSenderToDatabase(currentSenderEmail, userId);
        } else {
          // Fehler
          let errorMessage = data.error || 'Unknown error sending email';
          
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `The email address "${senderEmailData.email}" was rejected by the server.`;
          }
          
          if (onEmailResult) {
            onEmailResult('error', errorMessage, currentBulkContact);
          }
        }
      } catch (error) {
        console.error("Error sending email:", error);
        
        if (onEmailResult) {
          onEmailResult('error', error.message || 'Unknown error sending email', currentBulkContact);
        }
        
        // Entferne diese E-Mail aus den ausstehenden Sendungen
        delete pendingSends.current[currentId];
      }
    })();
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // 🔥 NEUE FUNKTION: AI Tag basierend auf Topic nach E-Mail-Versand setzen
  const setAiTagAfterEmailSend = async (topic, contactId) => {
    try {
      console.log('🏷️ [BULK-POST-EMAIL-TAG] Checking if topic has associated AI Tag:', topic);
      
      if (!userId || !contactId || !topic) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Missing required parameters');
        return false;
      }
      
      // Erweiterte Suche: Prüfe sowohl in selectedTopics als auch in customTopics
      let matchingTopic = null;
      
      // 1. Suche in allen userTopics (sowohl selectedTopics als auch customTopics)
      matchingTopic = Object.values(userTopics).find(topicData => {
        return topicData.topic === topic || topicData.type === topic;
      });
      
      // 2. Falls nicht gefunden, erweiterte Suche in der Struktur
      if (!matchingTopic && userTopics.selectedTopics) {
        matchingTopic = Object.values(userTopics.selectedTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      // 3. Falls immer noch nicht gefunden, suche in customTopics
      if (!matchingTopic && userTopics.customTopics) {
        matchingTopic = Object.values(userTopics.customTopics).find(topicData => {
          return topicData.topic === topic || topicData.type === topic;
        });
      }
      
      if (!matchingTopic) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] No matching topic found in userTopics for:', topic);
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Available topics:', {
          allTopics: Object.values(userTopics).map(t => ({ topic: t.topic, type: t.type, hasAiTagId: !!t.aiTagId })),
          selectedTopics: userTopics.selectedTopics ? Object.keys(userTopics.selectedTopics) : 'none',
          customTopics: userTopics.customTopics ? Object.keys(userTopics.customTopics) : 'none'
        });
        return false;
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] Found matching topic:', matchingTopic);
      
      // Prüfe, ob das Topic einen zugeordneten AI Tag hat
      if (!matchingTopic.aiTagId) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Topic has no associated AI tag - this is normal');
        return false;
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] ✅ Topic has associated AI tag ID:', matchingTopic.aiTagId);
      
      // Lade die AI Tag-Einstellungen
      const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${matchingTopic.aiTagId}`);
      const aiTagSnapshot = await new Promise((resolve) => {
        onValue(aiTagRef, resolve, { onlyOnce: true });
      });
      
      if (!aiTagSnapshot.exists()) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] AI tag not found for ID:', matchingTopic.aiTagId);
        return false;
      }
      
      const aiTagData = aiTagSnapshot.val();
      console.log('🏷️ [BULK-POST-EMAIL-TAG] AI tag data loaded:', aiTagData);
      
      // Erstelle den neuen Tag für den Kontakt
      const newTag = {
        tag: aiTagData.tag,
        name: aiTagData.name,
        color: aiTagData.color,
        icon: aiTagData.icon,
        appliedAt: new Date().toISOString(),
        appliedBy: 'post_email_send',
        appliedTopic: topic,
        ...(aiTagData.selectedTopic && { selectedTopic: aiTagData.selectedTopic })
      };
      
      // Referenz auf das Kontakt-Dokument in Firestore
      const contactDocRef = doc(firestore, `World/${userId}/db/${contactId}`);

      // Prüfe, ob es ein Overwrite-Tag ist
      if (aiTagData.overwriteOtherTags === true) {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Overwrite tag detected, removing all other tags');
        
        // Überschreibe alle Tags mit nur diesem einen Tag
        await updateDoc(contactDocRef, { tags: { [matchingTopic.aiTagId]: newTag } });
      } else {
        console.log('🏷️ [BULK-POST-EMAIL-TAG] Adding tag alongside existing tags');
        const contactSnap = await getDoc(contactDocRef);
        const existingTags = contactSnap.exists() ? contactSnap.data().tags || {} : {};
        await updateDoc(contactDocRef, { [`tags.${matchingTopic.aiTagId}`]: newTag, tags: { ...existingTags, [matchingTopic.aiTagId]: newTag } });
      }
      
      console.log('🏷️ [BULK-POST-EMAIL-TAG] ✅ AI tag successfully applied after email send');
      
      // 🔥 Informiere die Tabelle über die Tag-Änderung für sofortige UI-Aktualisierung
      if (onEmailResult) {
        onEmailResult('tag-update', `AI tag applied to contact`, { 
          id: contactId, 
          tagId: matchingTopic.aiTagId,
          tagData: newTag,
          _optimistic_update: true,
          _prevent_toast: true
        });
      }
      
      return true;
      
    } catch (error) {
      console.error('🏷️ [BULK-POST-EMAIL-TAG] Error setting AI tag after email send:', error);
      return false;
    }
  };

  // CC-Status für eine E-Mail umschalten
  const toggleCcEmail = (email) => {
    if (ccEmails.includes(email)) {
      setCcEmails(ccEmails.filter(e => e !== email));
    } else {
      if (email !== selectedEmail) {
        setCcEmails([...ccEmails, email]);
      }
    }
  };

  // E-Mail-Adressen des aktuellen Kontakts
  const getCurrentEmailAddresses = () => {
    if (currentBulkContact && currentBulkContact.lead_email) {
      return currentBulkContact.lead_email.split(',').map(email => email.trim()).filter(Boolean);
    }
    return [];
  };

  const currentContactEmails = getCurrentEmailAddresses();

  // Topic-Änderung behandeln
  const handleTopicChange = (topic) => {
    console.log('📧 [BULK-TOPIC-CHANGE] Manual topic change from', selectedTopic, 'to', topic);
    console.log('📧 [BULK-TOPIC-CHANGE] Current contact:', currentBulkContact?.business_name);
    console.log('📧 [BULK-TOPIC-CHANGE] Contact has AI tags:', !!currentBulkContact?.tags);
    
    // Topic aktualisieren
    setSelectedTopic(topic);
    
    // Direkt den Betreff aktualisieren, wenn ein Kontakt vorhanden ist und der Benutzer ihn nicht bearbeitet hat
    if (currentBulkContact && !userEditedSubject) {
      console.log('📧 [BULK-TOPIC-CHANGE] Updating subject line for manual topic change');
      // Verzögerung für bessere UI-Reaktion
      setTimeout(() => {
        updateSubjectInputDirectly(getTopicSubjectLine(currentBulkContact));
      }, 10);
    }
  };

  return (
    <div className="emailAiPopupContainer bulk-mode">
      <div className="emailAiPopupHeader">
        <h2>AI E-Mail Manager (Bulk-Modus)</h2>
        <button className="emailAiPopupCloseButton" onClick={handleClose}>×</button>
      </div>
      
      <BulkModeProgress 
        currentBulkIndex={currentBulkIndex}
        totalContacts={updatedBulkContacts.length}
        goToPreviousBulkContact={goToPreviousBulkContact}
        goToNextBulkContact={goToNextBulkContact}
        currentBulkContact={currentBulkContact}
        sentEmails={sentEmails}
        canGoToPrevious={canGoToPrevious}
        bulkContacts={updatedBulkContacts}
        onDeleteContact={handleDeleteContact}
      />
      
      <div className="emailAiPopupContent">
        {/* LINKE SPALTE: Topics, Style, Context */}
        <div className="emailAiPopupLeftColumn">
          <div className="emailAiPopupTopicSection">
            <h3 className="emailAiPopupSectionTitle">Topic</h3>
            <div className="emailAiPopupTopicButtons">
              {["Introduce Yourself", "Follow Up", "Second Follow Up", "Third Follow Up"].map(topic => (
                <button
                  key={topic}
                  className={`emailAiPopupTopicButton ${selectedTopic === topic ? "active" : ""}`}
                  onClick={() => handleTopicChange(topic)}
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>

          <div className="emailAiPopupStyleSection">
            <h3 className="emailAiPopupSectionTitle">Style</h3>
            <div className="emailAiPopupStyleButtons">
              <button
                onClick={() => setSelectedStyle("Professional & Supportive")}
                className={selectedStyle === "Professional & Supportive" ? "activeStyle" : ""}
              >
                <strong>Professional</strong>
                <span className="styleDescription">Formal, präzise, geschäftlich</span>
              </button>
              <button
                onClick={() => setSelectedStyle("Friendly & Authentic")}
                className={selectedStyle === "Friendly & Authentic" ? "activeStyle" : ""}
              >
                <strong>Casual</strong>
                <span className="styleDescription">Locker, warm, persönlich</span>
              </button>
            </div>
          </div>

          <div className="emailAiPopupContextSection">
            <h3 className="emailAiPopupSectionTitle">Description</h3>
            <div className="emailAiPopupContextWrapper">
              <textarea
                className="emailAiPopupContextInput"
                placeholder="Kontext wird für jeden Kontakt automatisch geladen..."
                value={contextInfo}
                onChange={(e) => setContextInfo(e.target.value)}
              ></textarea>
              
              {/* Social Media Buttons */}
              {currentBulkContact && (
                <div className="bonusclub-social-buttons-container">
                  {currentBulkContact.lead_website && (
                    <a 
                      href={formatURL(currentBulkContact.lead_website, 'https://')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-website"
                      title="Website besuchen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_instagram && (
                    <a 
                      href={formatURL(currentBulkContact.lead_instagram, 'https://instagram.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-instagram"
                      title="Instagram Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_facebook && (
                    <a 
                      href={formatURL(currentBulkContact.lead_facebook, 'https://facebook.com/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-facebook"
                      title="Facebook Seite"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                      </svg>
                    </a>
                  )}
                  {currentBulkContact.lead_linkedin && (
                    <a 
                      href={formatURL(currentBulkContact.lead_linkedin, 'https://linkedin.com/in/')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bonusclub-social-button bonusclub-social-linkedin"
                      title="LinkedIn Profil"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RECHTE SPALTE: Subject, Body, Extra Buttons, Generate/Send */}
        <div className="emailAiPopupRight">
          {/* Absender-E-Mail-Auswahl */}
          <div className="emailAiPopupBottomButtons">
            <button 
              className={`emailAiPopupGenerateButton ${isGenerating ? 'loading' : ''}`} 
              onClick={handleRegenerateForCurrentContact}
              disabled={isGenerating || isBulkGenerating}
              title="E-Mail neu generieren"
            >
              {isGenerating ? (
                <span className="emailAiPopupSpinner"></span>
              ) : (
                <img src="/img/star.svg" alt="AI" className="emailAiPopupGenerateIcon" />
              )}
            </button>
            <button
              className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() ? 'disabled' : ''}`}
              onClick={handleSend}
              disabled={isGenerating || isSending || !isEmailValid() || isBulkGenerating}
            >
              {isSending ? (
                <>
                  <div className="emailAiPopupSpinner"></div>
                  <span>Sending...</span>
                </>
              ) : sendSuccess ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>{currentBulkIndex === updatedBulkContacts.length - 1 ? 'Send & Finish' : 'Send & Next'}</span>
                  <img src="/img/sender.svg" alt="Send" className="emailAiPopupSendIcon" />
                </>
              )}
            </button>
          </div>
          <div className="emailAiPopupField">
            <label>From:</label>
            <div className="emailAiPopupSenderEmailsContainer">
              {senderEmails.length > 0 ? (
                <div className="emailAiPopupSenderEmailsScroll">
                  {senderEmails.map((item) => (
                    <button 
                      key={item.email}
                      className={`emailAiPopupSenderEmailButton ${selectedSenderEmail === item.email ? 'active' : ''}`}
                      onClick={() => setSelectedSenderEmail(item.email)}
                    >
                      {item.sender_name 
                        ? `${item.sender_name} <${item.email}>` 
                        : item.email}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="emailAiPopupNoEmails">No sender emails configured.</p>
              )}
            </div>
          </div>

          {/* Empfänger-E-Mail-Anzeige */}
          <div className="emailAiPopupRecipientSection">
            <h3 className="emailAiPopupSectionTitle">To:</h3>
            <div className="emailAiPopupTopButtons">
              {currentContactEmails.map((email, index) => (
                <div key={index} className="emailAiPopupButtonWrapper">
                  <button
                    className={`emailAiPopupButton ${email === selectedEmail ? 'active' : ''}`}
                    onClick={() => {
                      if (ccEmails.includes(email)) {
                        setCcEmails(ccEmails.filter(e => e !== email));
                      }
                      setSelectedEmail(email);
                    }}
                  >
                    {email}
                  </button>
                  
                  {email !== selectedEmail && (
                    <button 
                      className={`emailAiPopupCcButton ${ccEmails.includes(email) ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCcEmail(email);
                      }}
                      title={ccEmails.includes(email) ? "Remove from CC" : "Add to CC"}
                    >
                      {ccEmails.includes(email) ? "CC ✓" : "CC"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Betreff und Inhalt */}
          <input
            className="emailAiPopupSubjectInput"
            placeholder="Subject line"
            value={subjectLine}
            onChange={(e) => {
              setSubjectLine(e.target.value);
              setUserEditedSubject(true);
            }}
            ref={subjectInputRef}
          />

      
<div className={`emailAiPopupTexfieldwrapper ${isGenerating || isBulkGenerating ? 'loading' : ''}`} style={{position: 'relative'}}>

{/* Shimmer-Container für die Loading-Animation */}
<div className="salesgod-loading-container">
  <img src="/img/salesgod_dark.webp" alt="AI" className="salesgod-loading-logo" />
  Generating email...
</div>

<div className="emailAiPopupFormatButtons">
            <button 
              type="button" 
              onClick={() => {
                const textarea = document.getElementById('emailContent');
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = bodyContent.substring(start, end);
                const newText = bodyContent.substring(0, start) + 
                               `<b>${selectedText}</b>` + 
                               bodyContent.substring(end);
                setBodyContent(newText);
                setUserEditedBody(true);
              }}
              title="Bold"
            >
              B
            </button>
            <button 
              type="button" 
              onClick={() => {
                const textarea = document.getElementById('emailContent');
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = bodyContent.substring(start, end);
                const newText = bodyContent.substring(0, start) + 
                               `<i>${selectedText}</i>` + 
                               bodyContent.substring(end);
                setBodyContent(newText);
                setUserEditedBody(true);
              }}
              title="Italic"
            >
              I
            </button>
          </div>
          <textarea
            id="emailContent"
            className="emailAiPopupTextArea"
            value={bodyContent}
            onChange={(e) => {
              setBodyContent(e.target.value);
              setUserEditedBody(true);
            }}
            placeholder={isBulkGenerating ? "Generating emails..." : "Email content"}
          >

            
          </textarea>
          
          
</div>

        </div>
      </div>
      
      {/* Bulk-Modus-spezifische Komponenten */}
      {isBulkGenerating && (
        <BulkGenerationLoading 
          progress={bulkGenerationProgress} 
          totalEmails={updatedBulkContacts.length}
          currentEmail={Math.ceil((bulkGenerationProgress / 100) * Math.min(5, updatedBulkContacts.length))}
        />
      )}
    </div>
  );
};

export default BulkModeEmail; 